import { Tooltip } from '@vkontakte/vkui';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { t } from '../../i18n';
import styles from './ui.module.css';

/**
 * Подпись поля формы.
 *
 * Отдаётся внутрь `<FormItem top={…}>`: сам FormItem остаётся вкуишный — он
 * держит отступы, статус ошибки и связь подписи с полем через id. Своя здесь
 * только типографика: у VKUI подпись набрана пятнадцатым кеглем, и на экране
 * из четырнадцати полей она спорит с самим введённым текстом. В макете это
 * мелкая строчная подпись, а обязательность названа словом, а не звёздочкой —
 * красная звёздочка ничего не объясняет тому, кто видит анкету впервые.
 *
 * `hint` — пояснение к полю, и живёт оно ПОД знаком вопроса, а не строкой под
 * полем. Строкой оно читалось как часть анкеты: в редакторе четырнадцать
 * полей, и под каждым висело по предложению служебного текста. Под знаком
 * вопроса его открывает тот, кому оно нужно.
 *
 * Ошибка так не прячется никогда: она остаётся в `bottom` у FormItem, на
 * виду.
 */
export function FieldLabel({
  children,
  required = false,
  counter,
  hint,
}: {
  children: ReactNode;
  required?: boolean;
  /** Счётчик символов у полей с пределом: он живёт в шапке, а не в подсказке. */
  counter?: ReactNode;
  hint?: ReactNode;
}) {
  return (
    <span className={styles.fieldLabel}>
      <span className={styles.fieldLabelText}>{children}</span>
      {required ? <span className={styles.fieldRequired}>{t('ui.field.required')}</span> : null}
      {hint === undefined ? null : <FieldTip>{hint}</FieldTip>}
      {counter === undefined ? null : <span className={styles.fieldCounter}>{counter}</span>}
    </span>
  );
}

/**
 * Знак вопроса у подписи: по нажатию раскрывает пояснение.
 *
 * Пузырь ставит ВКУИШНЫЙ `Tooltip` — это ровно та механика, которую нет
 * смысла писать заново: он считает место у края экрана и переворачивает
 * подсказку сам (у нашего самодельного пузыря она вылезала за правый край).
 * Наше здесь только оформление: цвет коробки и текста задаём его же
 * переменными, а `usePortal={false}` оставляет пузырь внутри подписи, иначе
 * переменные до него не доходят — он уезжает в портал у корня страницы.
 *
 * Показом управляем сами: нажатием, а не наведением. Наведения на телефоне
 * нет, и подсказка, которую видно только с мышью, — это подсказка, которой
 * нет.
 */
function FieldTip({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const holderRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (!open) return undefined;

    const onPointerDown = (event: PointerEvent) => {
      const holder = holderRef.current;
      if (holder !== null && !holder.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };

    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  return (
    <span className={styles.fieldTip} ref={holderRef}>
      <Tooltip
        shown={open}
        description={children}
        appearance="neutral"
        placement="bottom-end"
        usePortal={false}
        maxWidth={240}
      >
        <button
          type="button"
          className={styles.fieldTipButton}
          aria-label={t('ui.field.hint')}
          aria-expanded={open}
          onClick={() => setOpen((value) => !value)}
        >
          ?
        </button>
      </Tooltip>
    </span>
  );
}
